import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

// Types based on your reference files
export interface LoginRequest {
  email: string;
  password: string;
}

export interface UserMetadata {
  account_id: string;
  email_verified: boolean;
  first_name: string;
  last_name: string;
  role: string;
  user_type: string;
}

export interface UserMessage {
  id: string;
  email: string;
  user_metadata: UserMetadata;
  access_token: string;
  refresh_token: string;
  // Add other fields as needed
}

export interface LoginResponse {
  status: boolean;
  message?: UserMessage;
  restaurant_parent_id?: string;
}

// Default restaurant ID constant
export const DEFAULT_RESTAURANT_ID = '4bebbb8a-a2bf-4d53-b287-842c3c5be847';

export interface LoginState {
  data: {
    status: boolean;
    message?: UserMessage;
    restaurant_parent_id?: string;
  };
  loading: boolean;
  error: string | null;
}

export interface LogoutState {
  data: any[];
  loading: boolean;
  error: string | null;
}

// Helper functions for localStorage
const loadAuthState = (): LoginState['data'] => {
  try {
    const serializedState = localStorage.getItem('authState');
    if (serializedState === null) {
      return { status: false };
    }
    return JSON.parse(serializedState);
  } catch (err) {
    return { status: false };
  }
};

const saveAuthState = (state: LoginState['data']) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('authState', serializedState);
  } catch (err) {
    // Ignore write errors
  }
};

const clearAuthState = () => {
  try {
    localStorage.removeItem('authState');
  } catch (err) {
    // Ignore errors
  }
};

// Login slice matching your reference pattern with persistence
const loginInitialState: LoginState = {
  data: loadAuthState(), // Load from localStorage on initialization
  loading: false,
  error: null,
};

const loginSlice = createSlice({
  name: 'auth',
  initialState: loginInitialState,
  reducers: {
    getLoginRequest: (state, action: PayloadAction<LoginRequest>) => {
      state.loading = true;
      state.error = null;
    },
    getLoginSuccess: (state, action: PayloadAction<LoginResponse>) => {
      state.loading = false;
      state.data = action.payload;
      state.error = null;
      // Save to localStorage when login is successful
      saveAuthState(action.payload);
    },
    getLoginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    getLoginRequestReset: (state) => {
      state.loading = false;
      state.data = { status: false };
      state.error = null;
      // Clear localStorage when login is reset
      clearAuthState();
    }
  }
});

// Logout slice matching your reference pattern
const logoutInitialState: LogoutState = {
  data: [],
  loading: false,
  error: null,
};

const logoutSlice = createSlice({
  name: 'logoutData',
  initialState: logoutInitialState,
  reducers: {
    getLogoutRequest: (state) => {
      state.loading = true;
    },
    getLogoutSuccess: (state) => {
      state.loading = false;
      state.data = [];
      state.error = null;
    },
    getLogoutFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    getLogoutRequestReset: (state) => {
      state.loading = false;
      state.data = [];
      state.error = null;
    }
  }
});

// Export login actions
export const {
  getLoginRequest,
  getLoginSuccess,
  getLoginFailure,
  getLoginRequestReset
} = loginSlice.actions;

// Export logout actions
export const {
  getLogoutRequest,
  getLogoutSuccess,
  getLogoutFailure,
  getLogoutRequestReset
} = logoutSlice.actions;

// Export reducers
export const loginReducer = loginSlice.reducer;
export const logoutReducer = logoutSlice.reducer;

// Default export for backward compatibility
export default loginSlice.reducer;
